import { useState } from 'react';
import { SectionBox } from '../helpers';
import { Typography } from '@sweep-io/sweep-design';
import { Box, Collapse } from '@mui/material';
import { ExpandButtonSimple } from '../../../common/ExpandButtonSimple';
import { useRunOnce } from '../../../common/useRunOnce';
import { selectLatestDeployment } from '../../../../reducers/documentation/documentationReducer';
import { useSelector } from 'react-redux';
import { DevOpsCenterDeploymentStatusCode } from '@server/devops-center';
import { DeploymentStatusIcon } from '../../../pages/devops-center/StatusIcon';
import {
  SWEEP_AUTOMATION_TEMPLATE_TYPES,
  SWEEP_DEDUP_MATCHING_TEMPLATE_TYPES,
  ConfigurationType,
  ConfigurationTypesWithIcon,
} from '../types';
import { AutomationTemplate } from './templates/AutomationTemplate';
import { DedupAndMatchingTemplate } from './templates/DedupAndMatchingTemplate';
import { RollupTemplate } from './templates/RollupTemplate';
import { useDependencies } from '../hooks/useDependencies';
import { SweepElementTypes } from '../../types';
import { ReadOnlyDedupMatching } from '@server/read-only-elements.types';
import { CenteredCircularProgress } from '../../../common/CenteredCircularProgress';
import { useSearchContext } from '../search/SearchContext';
import { HighlightedSection } from '../search/highlight/HighlightedSection';
import { useSectionExpansion } from '../search/useSectionExpansion';
import { BASE_SECTION_EXPANSION } from '../search/consts';

interface LatestDeployedSectionProps {
  parentElementId: string;
  crmOrgId: string;
  sweepElementType: SweepElementTypes;
}

export const LatestDeployedSection = ({
  crmOrgId,
  parentElementId,
  sweepElementType,
}: LatestDeployedSectionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const { searchTerm, sectionExpansion } = useSearchContext();

  const { isExpanded, setIsExpanded } = useSectionExpansion({
    searchTerm,
    sectionExpanded: sectionExpansion.latestDeployed,
    defaultExpanded: BASE_SECTION_EXPANSION.latestDeployed,
  });

  const latestDeployment = useSelector(selectLatestDeployment(parentElementId));

  const isAutomation = SWEEP_AUTOMATION_TEMPLATE_TYPES.includes(sweepElementType);
  const isDedupMatching = SWEEP_DEDUP_MATCHING_TEMPLATE_TYPES.includes(sweepElementType);
  const isRollup = sweepElementType === ConfigurationType.rollups;

  const LatestDeploymentComponent = (
    <>
      {isAutomation && (
        <AutomationTemplate
          elementType={sweepElementType as ConfigurationTypesWithIcon}
          readOnlyVersion={latestDeployment}
        />
      )}
      {isDedupMatching && (
        <DedupAndMatchingTemplate
          elementType={sweepElementType as ConfigurationTypesWithIcon}
          readOnlyVersion={latestDeployment as ReadOnlyDedupMatching}
        />
      )}
      {isRollup && <RollupTemplate elementId={parentElementId} crmOrgId={crmOrgId} />}
    </>
  );

  const { fetchLatestDeployedVersion } = useDependencies(crmOrgId);

  useRunOnce(async () => {
    if (!latestDeployment && !isLoading && !isError) {
      setIsLoading(true);
      setIsError(false);
      try {
        await fetchLatestDeployedVersion(sweepElementType, parentElementId, crmOrgId);
        setIsLoading(false);
      } catch (e) {
        setIsError(true);
        setIsLoading(false);
      }
    }
  });

  return (
    <SectionBox data-section="latestDeployed">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        sx={{ cursor: 'pointer' }}
      >
        <Box display="flex" gap={1} alignItems="center">
          <DeploymentStatusIcon status={DevOpsCenterDeploymentStatusCode.FullySynced} />
          <Typography variant="h5-medium">Latest deployed version</Typography>
        </Box>
        <ExpandButtonSimple isExpanded={isExpanded} />
      </Box>
      <Collapse in={isExpanded}>
        <Box pt={1}>
          {isLoading && <CenteredCircularProgress size={20} />}
          {!isLoading && isError && (
            <Typography variant="caption">Something went wrong, try again later</Typography>
          )}
          {!isLoading && !isError ? (
            <HighlightedSection section="latestDeployed">
              {LatestDeploymentComponent}
            </HighlightedSection>
          ) : (
            <></>
          )}
        </Box>
      </Collapse>
    </SectionBox>
  );
};
